/**
 * Dashboard Page Controller - Type-Safe Version
 * Coordinates dashboard modules and handles main page logic
 */
import { buildLoginRedirect, getCurrentPath, optionalAuthPage, type PageAuthContext } from '../lib/page-runtime';

(function() {
  let newTokenValue = '';
  let dashboardCache: any = null;
  let dashboardDataLoaded = false;
  let isMaintainerDashboardUser = false;
  let dashboardUser: PageAuthContext['user'] = null;

  // ============================================================================
  // Data Loading
  // ============================================================================

  async function loadDashboardData() {
    const result = await window.DashboardData.loadDashboardData();
    if (!result.ok) {
      showDashboardError(result.error);
      return;
    }
    dashboardCache = result.data;
    window.DashboardAdmin.setCurrentUser(dashboardCache.user);
    window.DashboardNextStep.render(dashboardCache, dashboardUser);
    window.DashboardRenderers.renderIssues(dashboardCache.issues);
    window.DashboardRenderers.renderSubmissions(dashboardCache.submissions);
    window.DashboardRenderers.renderSkills(dashboardCache.skills);
    window.DashboardRenderers.renderAccessTokens(dashboardCache.access_tokens);
    syncAccessTokenCreationGate();
  }

  function showDashboardError(message: string) {
    const sectionIds = ['my-issues-list', 'my-submissions-list', 'my-skills-list', 'access-tokens-list'];
    sectionIds.forEach(function(id) {
      const el = document.getElementById(id);
      if (el) el.innerHTML = '<p class="text-center py-8 text-red-400 text-sm">' + escapeHtml(message) + '</p>';
    });
  }

  // ============================================================================
  // Authentication & Initialization
  // ============================================================================

  function renderDashboardAuth(ctx: PageAuthContext) {
    const loggedIn = ctx.loggedIn;
    const user = ctx.user;
    const pending = document.getElementById('dashboard-auth-pending');
    if (pending) pending.remove();
    const gate = document.getElementById('dashboard-auth-gate');
    const content = document.getElementById('dashboard-content');
    if (loggedIn) {
      if (gate) gate.remove();
      if (content) content.style.display = '';
    } else {
      if (content) content.remove();
      if (gate) {
        const signInLink = gate.querySelector<HTMLAnchorElement>('a[href^="/login"]');
        if (signInLink) signInLink.href = buildLoginRedirect(getCurrentPath());
        gate.style.display = '';
      }
    }
    if (loggedIn && user) {
      dashboardUser = user;
      isMaintainerDashboardUser = user.role === 'maintainer';
      window.DashboardAdmin.setCurrentUser(user);
      document.getElementById('dashboard-username')!.textContent = 'Welcome, ' + user.username + (isMaintainerDashboardUser ? ' (Maintainer)' : '');

      const adminTab = document.getElementById('admin-tab');
      if (adminTab) {
        if (isMaintainerDashboardUser) {
          adminTab.classList.remove('hidden');
        } else {
          adminTab.remove();
        }
      }
      const adminPanel = document.getElementById('tab-admin');
      if (!isMaintainerDashboardUser && adminPanel) adminPanel.remove();

      document.getElementById('mcp-url')!.textContent = window.location.origin + '/mcp';
      syncAccessTokenCreationGate();
      if (!dashboardDataLoaded) {
        dashboardDataLoaded = true;
        const dashboardLoad = loadDashboardData();
        window.DashboardHeatmap.loadHeatmap(user.username);
        if (isMaintainerDashboardUser) {
          dashboardLoad.then(() => window.DashboardAdmin.loadAdminData());
        }
        handleDashboardHash();
      }
    } else {
      dashboardUser = null;
      isMaintainerDashboardUser = false;
      window.DashboardAdmin.setCurrentUser(null);
      const adminTab = document.getElementById('admin-tab');
      if (adminTab) adminTab.classList.add('hidden');
    }
  }

  // ============================================================================
  // Tab Navigation
  // ============================================================================

  type DashboardSection = 'issues' | 'submissions' | 'skills' | 'access-tokens' | 'admin';

  function isDashboardSection(tab: string): tab is DashboardSection {
    return tab === 'issues' || tab === 'submissions' || tab === 'skills' || tab === 'access-tokens' || tab === 'admin';
  }

  function syncDashboardSectionUrl(tab: string): void {
    if (!isDashboardSection(tab)) return;
    const nextSearch = window.DashboardNavigation.buildDashboardSectionSearch(window.location.search, tab);
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;
    const nextUrl = window.location.pathname + nextSearch + window.location.hash;
    if (nextUrl !== currentUrl) window.history.replaceState(null, '', nextUrl);
  }

  function setDashboardPanelVisible(panel: HTMLElement, visible: boolean) {
    panel.style.display = visible ? '' : 'none';
    panel.hidden = !visible;
    panel.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (visible) {
      panel.removeAttribute('inert');
    } else {
      panel.setAttribute('inert', '');
    }
  }

  function switchTab(tab: string) {
    if (tab === 'admin' && !isMaintainerDashboardUser) {
      return;
    }

    const targetTab = document.querySelector('[data-tab="'+tab+'"]');
    const targetPanel = document.getElementById('tab-'+tab);
    if (!targetTab || !targetPanel || targetTab.classList.contains('hidden')) return;

    document.querySelectorAll('.dash-tab').forEach(t => t.classList.remove('active'));
    targetTab.classList.add('active');
    document.querySelectorAll('.tab-content').forEach(t => setDashboardPanelVisible(t as HTMLElement, false));
    setDashboardPanelVisible(targetPanel, true);

    if (tab === 'access-tokens') {
      window.setTimeout(function() {
        document.getElementById('access-token-name')?.focus();
      }, 0);
    }
    syncDashboardSectionUrl(tab);
  }

  function handleDashboardHash() {
    const requestedSection = window.DashboardNavigation.resolveDashboardSection(window.location.search, window.location.hash);
    if (requestedSection === 'access-tokens') {
      switchTab('access-tokens');
      return;
    }
    if (requestedSection === 'admin' && isMaintainerDashboardUser) {
      switchTab('admin');
      return;
    }
    if (requestedSection && requestedSection !== 'admin') {
      switchTab(requestedSection);
      return;
    }
    if (isMaintainerDashboardUser && window.DashboardAdmin.hasAdminUsersViewState(window.location.search)) {
      switchTab('admin');
    }
  }

  // ============================================================================
  // Access Token Management
  // ============================================================================

  function accessTokenAlertClasses(tone: 'amber' | 'error' | 'success'): string {
    if (tone === 'amber') return 'card p-5 mb-6 border border-amber-500/30 bg-amber-500/10';
    if (tone === 'success') return 'card p-5 mb-6 border border-brand-500/30 bg-brand-500/10';
    return 'card p-5 mb-6 border border-red-500/30 bg-red-500/10';
  }

  function clearAccessTokenActionMessage(): void {
    const alert = document.getElementById('token-action-alert');
    if (!alert) return;
    alert.className = 'hidden card p-5 mb-6';
    alert.textContent = '';
    delete alert.dataset.accessTokenGate;
  }

  function clearEditableAccessTokenActionMessage(): void {
    const alert = document.getElementById('token-action-alert');
    if (alert?.dataset.accessTokenGate === 'email-verification') return;
    clearAccessTokenActionMessage();
  }

  function renderAccessTokenActionMessage(message: ReturnType<typeof window.DashboardRenderers.buildAccessTokenCreationErrorMessage>, marker?: string): void {
    const alert = document.getElementById('token-action-alert');
    if (!alert) return;
    alert.className = accessTokenAlertClasses(message.tone);
    alert.textContent = '';
    if (marker) {
      alert.dataset.accessTokenGate = marker;
    } else {
      delete alert.dataset.accessTokenGate;
    }

    const header = document.createElement('div');
    header.className = 'flex items-center gap-2 mb-2';
    const icon = document.createElement('i');
    const iconToneClass = message.tone === 'amber'
      ? ' text-amber-300'
      : message.tone === 'success'
        ? ' text-brand-300'
        : ' text-red-300';
    icon.className = 'fas ' + message.icon + iconToneClass;
    const title = document.createElement('span');
    title.className = 'font-semibold text-white text-sm';
    title.textContent = message.title;
    header.append(icon, title);

    const body = document.createElement('p');
    body.className = 'text-sm text-zinc-300';
    body.textContent = message.body;

    alert.append(header, body);
    if (message.recovery) {
      const facts = document.createElement('div');
      facts.className = 'mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4';
      [
        ['Cause', message.recovery.cause],
        ['Impact', message.recovery.impact],
        ['Required authority', message.recovery.authority],
        ['Next action', message.recovery.nextAction],
      ].forEach(function([label, value]) {
        const item = document.createElement('div');
        const itemLabel = document.createElement('div');
        itemLabel.className = 'text-[11px] font-semibold uppercase tracking-wide text-zinc-500';
        itemLabel.textContent = label;
        const itemValue = document.createElement('div');
        itemValue.className = 'mt-1 text-sm text-zinc-200';
        itemValue.textContent = value;
        item.append(itemLabel, itemValue);
        facts.appendChild(item);
      });
      alert.appendChild(facts);
    }
    if (message.actionHref && message.actionLabel) {
      const action = document.createElement('a');
      action.href = message.actionHref;
      action.className = 'btn-primary text-sm inline-flex mt-3';
      action.textContent = message.actionLabel;
      alert.appendChild(action);
    }
  }

  function isAccessTokenCreationBlockedByEmail(): boolean {
    return dashboardUser?.email_verification_required === true && !dashboardUser.email_verified_at;
  }

  function isAccessTokenNameReady(input: HTMLInputElement | null): boolean {
    return Boolean(input?.value.trim());
  }

  function syncAccessTokenCreationGate(): void {
    const blocked = isAccessTokenCreationBlockedByEmail();
    const input = document.getElementById('access-token-name') as HTMLInputElement | null;
    const button = document.getElementById('access-token-create-button') as HTMLButtonElement | null;
    if (input) input.disabled = blocked;
    if (button) {
      const disabled = blocked || !isAccessTokenNameReady(input);
      button.disabled = disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }
    if (blocked) {
      const message = window.DashboardRenderers.buildAccessTokenCreationErrorMessage('Email verification required before creating access tokens.');
      renderAccessTokenActionMessage(message, 'email-verification');
      return;
    }
    const alert = document.getElementById('token-action-alert');
    if (alert?.dataset.accessTokenGate === 'email-verification') {
      clearAccessTokenActionMessage();
    }
  }

  function setSubmitButtonBusy(button: HTMLButtonElement | null, busyText: string): () => void {
    if (!button) return () => {};
    const originalHtml = button.innerHTML;
    const originalAriaBusy = button.getAttribute('aria-busy');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = busyText;
    return function restoreSubmitButton() {
      button.disabled = false;
      button.innerHTML = originalHtml;
      if (originalAriaBusy === null) {
        button.removeAttribute('aria-busy');
      } else {
        button.setAttribute('aria-busy', originalAriaBusy);
      }
    };
  }

  function setAccessTokenNameInputBusy(input: HTMLInputElement | null): () => void {
    if (!input) return () => {};
    const originalDisabled = input.disabled;
    const originalAriaDisabled = input.getAttribute('aria-disabled');
    input.disabled = true;
    input.setAttribute('aria-disabled', 'true');
    return function restoreAccessTokenNameInput() {
      input.disabled = originalDisabled;
      if (originalAriaDisabled === null) {
        input.removeAttribute('aria-disabled');
      } else {
        input.setAttribute('aria-disabled', originalAriaDisabled);
      }
    };
  }

  async function createAccessToken(e: Event) {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    if (isAccessTokenCreationBlockedByEmail()) {
      syncAccessTokenCreationGate();
      showToast('Verify your email before creating access tokens.', 'error');
      return;
    }
    const nameInput = document.getElementById('access-token-name') as HTMLInputElement | null;
    const name = nameInput?.value.trim() || '';
    const scopeType = (document.getElementById('access-token-scope') as HTMLSelectElement | null)?.value as 'account' | 'submission' | 'skill' | undefined;
    const resourceValue = (document.getElementById('access-token-resource-id') as HTMLInputElement | null)?.value;
    const expiryValue = (document.getElementById('access-token-expiry') as HTMLSelectElement | null)?.value;
    if (!name) {
      syncAccessTokenCreationGate();
      return;
    }
    const restoreSubmitButton = setSubmitButtonBusy(
      form.querySelector('button[type="submit"]') as HTMLButtonElement | null,
      'Creating...',
    );
    const restoreNameInput = setAccessTokenNameInputBusy(nameInput);

    try {
      const result = await window.DashboardData.createAccessToken({
        name,
        scope_type: scopeType,
        ...(resourceValue ? { scope_resource_id: Number(resourceValue) } : {}),
        ...(expiryValue ? { expires_in_seconds: Number(expiryValue) } : {}),
      });

      if (result.ok) {
        newTokenValue = result.data.token;
        clearAccessTokenActionMessage();
        const alert = document.getElementById('token-created-alert');
        if (alert) {
          alert.className = 'card p-5 mb-6 border-brand-500/30 glow-emerald';
          alert.innerHTML =
            '<div class="flex items-center gap-2 mb-2">' +
              '<i class="fas fa-check-circle text-brand-400"></i>' +
              '<span class="font-semibold text-white text-sm">Token Created - Save it now!</span>' +
            '</div>' +
            '<div class="bg-surface-0 rounded-lg p-3 font-mono text-sm text-brand-300 break-all" id="new-token-value"></div>' +
            '<div class="flex gap-2 mt-3">' +
              '<button onclick="copyNewToken()" class="btn-ghost text-xs"><i class="fas fa-copy mr-1"></i>Copy</button>' +
              '<p class="text-xs text-zinc-500 flex items-center"><i class="fas fa-warning mr-1 text-amber-400"></i>This token cannot be shown again</p>' +
            '</div>';
          document.getElementById('new-token-value')!.textContent = result.data.token;
          alert.classList.remove('hidden');
        }
        (document.getElementById('access-token-name') as HTMLInputElement).value = '';
        showToast('Access token created!');
        loadDashboardData();
      } else {
        const message = window.DashboardRenderers.buildAccessTokenCreationErrorMessage(result.error);
        renderAccessTokenActionMessage(message);
        showToast(message.body, 'error');
      }
    } catch(e) {
      const message = window.DashboardRenderers.buildAccessTokenCreationErrorMessage('Network error. Check your connection and try again.');
      renderAccessTokenActionMessage(message);
      showToast(message.body, 'error');
    } finally {
      restoreNameInput();
      restoreSubmitButton();
      syncAccessTokenCreationGate();
    }
  }

  function toggleAccessTokenScope() {
    const scope = (document.getElementById('access-token-scope') as HTMLSelectElement | null)?.value || 'account';
    const resource = document.getElementById('access-token-resource-id') as HTMLInputElement | null;
    const expiry = document.getElementById('access-token-expiry') as HTMLSelectElement | null;
    if (resource) {
      resource.disabled = scope === 'account';
      resource.required = scope !== 'account';
      if (scope === 'account') resource.value = '';
    }
    if (expiry && scope !== 'account' && !expiry.value) expiry.value = '86400';
  }

  document.getElementById('access-token-name')?.addEventListener('input', function() {
    clearEditableAccessTokenActionMessage();
    syncAccessTokenCreationGate();
  });

  function copyNewToken() {
    navigator.clipboard.writeText(newTokenValue);
    showToast('Token copied!');
  }

  async function revokeToken(id: number, control?: HTMLButtonElement) {
    if (!confirm('Revoke this token? Connected clients will lose access.')) return;
    const restoreSubmitButton = setSubmitButtonBusy(control ?? null, 'Revoking...');
    try {
      const result = await window.DashboardData.revokeAccessToken(id);
      if (result.ok) {
        renderAccessTokenActionMessage(window.DashboardRenderers.buildAccessTokenRevokeSuccessMessage());
        showToast('Token revoked');
        loadDashboardData();
      } else {
        const message = window.DashboardRenderers.buildAccessTokenRevokeErrorMessage(result.error);
        renderAccessTokenActionMessage(message);
        showToast(message.body, 'error');
      }
    } catch(e) {
      const message = window.DashboardRenderers.buildAccessTokenRevokeErrorMessage('Network error. Check your connection and try again.');
      renderAccessTokenActionMessage(message);
      showToast(message.body, 'error');
    } finally {
      restoreSubmitButton();
    }
  }

  // ============================================================================
  // Global Exports
  // ============================================================================

  window.revokeToken = revokeToken;
  window.copyNewToken = copyNewToken;
  window.switchTab = switchTab;
  window.createAccessToken = createAccessToken;
  window.toggleAccessTokenScope = toggleAccessTokenScope;
  window.addEventListener('hashchange', handleDashboardHash);

  optionalAuthPage({
    onAuth: renderDashboardAuth,
    init: renderDashboardAuth,
  });
})();
